import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

// Global error handler to catch Firebase errors in Safari
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.code?.includes('messaging/') || 
      event.reason?.message?.includes('Firebase') ||
      event.reason?.message?.includes('messaging')) {
    console.warn('Firebase messaging error caught and handled:', event.reason);
    event.preventDefault(); // Prevent the error from crashing the app
  }
});

// Handle service worker registration
if ('serviceWorker' in navigator) {
  if (import.meta.env.DEV) {
    // Unregister service workers in development
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  } else {
    // VitePWA will handle service worker registration automatically
    console.log('VitePWA will handle service worker registration');
  }
}

// PWA Install Prompt Handler
let deferredPrompt: any = null;

// Debug PWA installation status
console.log('PWA: Checking installation status...');
console.log('PWA: User agent:', navigator.userAgent);
console.log('PWA: Is standalone mode:', window.matchMedia('(display-mode: standalone)').matches);

// Check if app is already installed
if (window.matchMedia('(display-mode: standalone)').matches) {
  console.log('PWA: App is already installed and running in standalone mode');
} else {
  console.log('PWA: App is not installed, setting up install prompt...');
}

// Listen for the beforeinstallprompt event
window.addEventListener('beforeinstallprompt', (e) => {
  console.log('PWA: beforeinstallprompt event fired');
  
  // Prevent the mini-infobar from appearing on mobile
  e.preventDefault();
  
  // Store the event so it can be triggered later
  deferredPrompt = e;
  
  // Show install prompt after user engagement
  setTimeout(() => {
    showInstallPrompt();
  }, 3000); // Show after 3 seconds of page load
});

// Function to show the native install prompt
function showInstallPrompt() {
  if (deferredPrompt) {
    console.log('PWA: Showing native install prompt');
    
    // Show the native install prompt directly
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('PWA: User accepted the install prompt');
      } else {
        console.log('PWA: User dismissed the install prompt');
      }
      
      // Clear the deferred prompt
      deferredPrompt = null;
    });
  } else {
    console.log('PWA: No deferred prompt available - app may already be installed or browser does not support installation');
  }
}

// Listen for the app to be installed
window.addEventListener('appinstalled', (e) => {
  console.log('PWA: App was installed successfully');
  // Clean up
  deferredPrompt = null;
});

// Add a fallback button for manual installation
window.addEventListener('load', () => {
  setTimeout(() => {
    // If no install prompt appeared after 5 seconds, show debug info
    if (!deferredPrompt) {
      console.log('PWA: No install prompt available after 5 seconds');
      console.log('PWA: Possible reasons:');
      console.log('- App is already installed');
      console.log('- Browser does not support PWA installation');
      console.log('- PWA criteria not met (service worker, manifest, HTTPS)');
      console.log('- User has previously dismissed the prompt');
      
      // Show a simple debug message in the browser
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        console.log('PWA: Try accessing Chrome menu > "Add to Home screen" or "Install app"');
      }
    }
  }, 5000);
});

// Import App component lazily
const App = lazy(() => import('./App'));
import './index.css';

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

createRoot(rootElement).render(
  <Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center">
    <div className="text-white text-xl">Loading...</div>
  </div>}>
    <App />
  </Suspense>
);